import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { PostHogPageviewTracker } from '../components/PostHogPageviewTracker';
import { LandingPage } from '../pages/LandingPage';
import { SearchPage } from '../pages/SearchPage';
import { DiscoveryPage } from '../pages/DiscoveryPage';
import { FacilityPage } from '../pages/FacilityPage';
import { ParkPage } from '../pages/ParkPage';
import { RequestsPage } from '../pages/RequestsPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { AuthPage } from '../pages/AuthPage';
import { SettingsPage } from '../pages/SettingsPage';
import { AboutPage } from '../pages/AboutPage';
import { AdminLayout } from '../layouts/AdminLayout';
import { AdminOverviewPage } from '../pages/admin/AdminOverviewPage';
import { AdminScrapingPage } from '../pages/admin/AdminScrapingPage';
import { AdminAvailabilityPage } from '../pages/admin/AdminAvailabilityPage';
import { AdminUsersPage } from '../pages/admin/AdminUsersPage';
import { AdminSystemPage } from '../pages/admin/AdminSystemPage';

function parseOptionalSearchString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseOptionalSearchNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <PostHogPageviewTracker />
      <Outlet />
    </>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>) => {
    const location = parseOptionalSearchString(search.location);
    const startDate = parseOptionalSearchString(search.startDate);
    const guests = parseOptionalSearchNumber(search.guests);
    const nights = parseOptionalSearchNumber(search.nights);

    return {
      ...(location ? { location } : {}),
      ...(startDate ? { startDate } : {}),
      ...(typeof guests === 'number' ? { guests } : {}),
      ...(typeof nights === 'number' ? { nights } : {}),
    };
  },
});

const discoveryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/discovery',
  component: DiscoveryPage,
  validateSearch: (search: Record<string, unknown>) => {
    const location = parseOptionalSearchString(search.location);
    const startDate = parseOptionalSearchString(search.startDate);
    const endDate = parseOptionalSearchString(search.endDate);
    const guests = parseOptionalSearchNumber(search.guests);

    return {
      ...(location ? { location } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      ...(typeof guests === 'number' ? { guests } : {}),
    };
  },
});

const facilityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/facility/$id',
  component: FacilityPage,
  validateSearch: (search: Record<string, unknown>) => {
    const startDate = parseOptionalSearchString(search.startDate);
    const nights = parseOptionalSearchNumber(search.nights);
    const guests = parseOptionalSearchNumber(search.guests);
    const fromParkId = parseOptionalSearchString(search.fromParkId);
    return {
      ...(startDate ? { startDate } : {}),
      ...(typeof nights === 'number' ? { nights } : {}),
      ...(typeof guests === 'number' ? { guests } : {}),
      ...(fromParkId ? { fromParkId } : {}),
    };
  },
});

const parkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/park/$id',
  component: ParkPage,
  validateSearch: (search: Record<string, unknown>) => {
    const startDate = parseOptionalSearchString(search.startDate);
    const nights = parseOptionalSearchNumber(search.nights);
    const guests = parseOptionalSearchNumber(search.guests);
    return {
      ...(startDate ? { startDate } : {}),
      ...(typeof nights === 'number' ? { nights } : {}),
      ...(typeof guests === 'number' ? { guests } : {}),
    };
  },
});

const requestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/requests',
  component: RequestsPage,
  beforeLoad: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw redirect({ to: '/auth' });
    }
  },
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationsPage,
  beforeLoad: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw redirect({ to: '/auth' });
    }
  },
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
  beforeLoad: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw redirect({ to: '/auth' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    const sessionId = typeof search.session_id === 'string' ? search.session_id : undefined;
    return {
      ...(sessionId ? { session_id: sessionId } : {}),
    };
  },
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: () => {
    throw redirect({ to: '/settings' });
  },
});

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
});

const adminBeforeLoad = async () => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw redirect({ to: '/' });
  }
  try {
    const base = import.meta.env.PROD && import.meta.env.VITE_API_PROXY_TARGET
      ? `${import.meta.env.VITE_API_PROXY_TARGET}/api/admin/me`
      : '/api/admin/me';
    const res = await fetch(base, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Not admin');
  } catch {
    throw redirect({ to: '/' });
  }
};

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: () => <AdminLayout><AdminOverviewPage /></AdminLayout>,
  beforeLoad: adminBeforeLoad,
});

const adminScrapingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/scraping',
  component: () => <AdminLayout><AdminScrapingPage /></AdminLayout>,
  beforeLoad: adminBeforeLoad,
});

const adminAvailabilityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/availability',
  component: () => <AdminLayout><AdminAvailabilityPage /></AdminLayout>,
  beforeLoad: adminBeforeLoad,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: () => <AdminLayout><AdminUsersPage /></AdminLayout>,
  beforeLoad: adminBeforeLoad,
});

const adminSystemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/system',
  component: () => <AdminLayout><AdminSystemPage /></AdminLayout>,
  beforeLoad: adminBeforeLoad,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  searchRoute,
  discoveryRoute,
  facilityRoute,
  parkRoute,
  requestsRoute,
  notificationsRoute,
  settingsRoute,
  billingRoute,
  authRoute,
  aboutRoute,
  adminRoute,
  adminScrapingRoute,
  adminAvailabilityRoute,
  adminUsersRoute,
  adminSystemRoute,
]);

export const router = createRouter({ routeTree });

export type RouterType = typeof router;
